// lib
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { DOMclosest } from '@aytm/lib/common/func'
import {
  KEYCODE_UP,
  KEYCODE_DOWN,
  KEYCODE_PGUP,
  KEYCODE_PGDOWN,
  KEYCODE_ENTER,
  KEYCODE_ESC
} from '@aytm/lib/common/const'

// constants
const SCROLL_TO_ITEM_MARGIN = 10

// styles
import '@aytm/lib/rfd-ui/styles/basic/dropdown.scss'

// <Component>
Dropdown.propTypes = {
  opened: PropTypes.bool.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  align: PropTypes.string, // left, right, fill (100% width)
  className: PropTypes.string,
  children: PropTypes.any,
  dropdownRef: PropTypes.any
}
export default function Dropdown(props) {
  const { opened, width, className, children, maxHeight, align, dropdownRef } = props
  return opened ? (
    <div
      ref={dropdownRef}
      className={`rfd-ui-dropdown${align ? ' rfd-ui-dropdown-align-' + align : ''}${className ? ' ' + className : ''}`}
      style={{ width }}
    >
      <div className="rfd-ui-dropdown-content" style={{ maxHeight }}>
        {children()}
      </div>
    </div>
  ) : null
}

export class DropdownList extends Component {
  static propTypes = {
    fetchItems: PropTypes.func.isRequired,
    filter: PropTypes.any,
    onSelectItem: PropTypes.func
  }

  shouldComponentUpdate(nextProps, nextState) {
    return this.props.filter != nextProps.filter || this.state.items != nextState.items
  }

  constructor(props) {
    super(props)
    this.setListRef = this.setListRef.bind(this);
    this.mouseDownListener = this.mouseDownListener.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.moveKeyboardFocus = this.moveKeyboardFocus.bind(this);
    this.state = {
      lastFilter: props.filter,
      items: props.fetchItems ? props.fetchItems({ filter: props.filter }) : []
    }
    this.listRef = null
    this.keyboardFocusIndex = 0
  }

  static getDerivedStateFromProps(props, state) {
    return state.lastFilter != props.filter
      ? {
          lastFilter: props.filter,
          items: props.fetchItems ? props.fetchItems({ filter: props.filter }) : []
        }
      : null
  }

  setListRef(ref) {
    if (this.listRef != ref) {
      if (this.listRef) {
        this.listRef.removeEventListener('mousedown', this.mouseDownListener, true)
      }
      this.listRef = ref
      if (this.listRef) {
        this.listRef.addEventListener('mousedown', this.mouseDownListener, true)
      }
    }
  }

  mouseDownListener(event) {
    if (0 === event.button) {
      event.preventDefault()
      const selectedItemElement = DOMclosest(event.target, '.rfd-ui-dropdown-list-item')
      if (selectedItemElement && this.state.items && this.props.onSelectItem) {
        const selectedItemIndex = parseInt(selectedItemElement.dataset.itemIndex, 10)
        if (!isNaN(selectedItemIndex)) {
          const selectedItem = this.state.items[selectedItemIndex]
          if (selectedItem) {
            this.props.onSelectItem(selectedItem)
          }
        }
      }
    }
  }

  onKeyDown(event) {
    switch (event.keyCode) {
      case KEYCODE_ESC: {
        event.preventDefault()
        this.moveKeyboardFocus(null)
        return 'close'
      }
      case KEYCODE_DOWN: {
        event.preventDefault()
        this.moveKeyboardFocus(Math.min(this.state.items.length - 1, this.keyboardFocusIndex + 1))
        return 'down'
      }
      case KEYCODE_UP: {
        event.preventDefault()
        this.moveKeyboardFocus(Math.max(0, this.keyboardFocusIndex - 1))
        return 'up'
      }
      case KEYCODE_PGDOWN: {
        event.preventDefault()
        this.moveKeyboardFocus(Math.min(this.state.items.length - 1, this.keyboardFocusIndex + 3))
        return 'pgdown'
      }
      case KEYCODE_PGUP: {
        event.preventDefault()
        this.moveKeyboardFocus(Math.max(0, this.keyboardFocusIndex - 3))
        return 'pgup'
      }
      case KEYCODE_ENTER: {
        event.preventDefault()
        if (this.props.onSelectItem) {
          const selectedItem = this.state.items[this.keyboardFocusIndex]
          if (selectedItem) {
            this.props.onSelectItem(selectedItem)
          }
          this.moveKeyboardFocus(null)
        }
        return 'select'
      }
      default: {
        return 'default'
      }
    }
  }

  moveKeyboardFocus(toIndex) {
    if (this.listRef) {
      // remove className from old focused item
      const focusedItems = this.listRef.querySelectorAll('.rfd-ui-dropdown-list-item-focused')
      for (const focusedItem of focusedItems) {
        focusedItem.classList.remove('rfd-ui-dropdown-list-item-focused')
      }
      // add className to the new focused item
      this.keyboardFocusIndex = toIndex
      if (null != this.keyboardFocusIndex) {
        const newFocuseditem = this.listRef.querySelectorAll('.rfd-ui-dropdown-list-item')[this.keyboardFocusIndex]
        if (newFocuseditem) {
          newFocuseditem.classList.add('rfd-ui-dropdown-list-item-focused')
          // scroll container to item
          const dropdownContent = DOMclosest(this.listRef, '.rfd-ui-dropdown-content')
          if (dropdownContent) {
            const containerTop = dropdownContent.scrollTop
            const containerHeight = dropdownContent.clientHeight
            const listHeight = this.listRef.scrollHeight
            const itemTop = newFocuseditem.offsetTop
            const itemHeight = newFocuseditem.offsetHeight
            if (itemTop < containerTop) {
              dropdownContent.scrollTop = Math.max(0, itemTop - SCROLL_TO_ITEM_MARGIN)
            } else if (itemTop + itemHeight > containerTop + containerHeight) {
              dropdownContent.scrollTop = Math.min(
                listHeight - containerHeight,
                itemTop + itemHeight + SCROLL_TO_ITEM_MARGIN - containerHeight
              )
            }
          }
        }
      }
    }
  }

  componentDidUpdate(_prevProps, prevState) {
    if (this.state.items.length != prevState.items.length) {
      this.moveKeyboardFocus(0)
    }
  }

  componentDidMount() {
    this.moveKeyboardFocus(0)
  }

  render() {
    const { items } = this.state
    const { filter } = this.props
    return (
      <div className="rfd-ui-dropdown-list" ref={this.setListRef}>
        {items.map((item, index) => (
          <div className="rfd-ui-dropdown-list-item" data-item-index={index} key={null == item.id ? index : item.id}>
            {item.render ? item.render({ index, item, filter }) : item.id}
          </div>
        ))}
      </div>
    )
  }
}
